import { CheckIcon } from '@heroicons/react/20/solid';
import { useMutationLogConsent, useMutationSaveDevis } from '@workspace/core-ts/api';
import { coordonneesContent, useContent } from '@workspace/core-ts/content';
import { BulleAide, ConsentRecontactLegal, isConsentementEmailRecueilli, CourtierEmailWarning, useCourtierEmailBlock, FieldInput, FieldRadioBlock, HorairesRecontact, Modal, Question, RgpdInfoBloc, SmartButton } from '@workspace/core-ts/components';
import { SmartFormGlobal, useGlobalForm, useSmartFormGlobal } from '@workspace/core-ts/contexts';
import { useAvenant, useSource } from '@workspace/core-ts/hooks';
import { OrdinateurTchat } from '@workspace/core-ts/icons-amo';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import CompanyFields from '../components/company';
import informationEntreprise from '../content/2-information-entreprise';
import informationInformations from '../content/8-informations';
import type { FormSchemaType } from '../schema';

export default function Informations() {
  const methods = useSmartFormGlobal<FormSchemaType>();
  const {
    actions: { saveData, getContent },
    meta,
  } = useGlobalForm();
  const mutationSaveDevis = useMutationSaveDevis(meta.token);
  const mutationLogConsent = useMutationLogConsent();
  const setSearchParams = useSearchParams()[1];
  const apporteur1Belair = methods.watch('DemandeTarif.Police.IdApporteur1');
  const { validateNotCourtierEmail } = useCourtierEmailBlock();
  const { isDirect, canAskContactConsent } = useSource();
  const isAvenant = useAvenant();
  const r = useContent();
  // Le consentement email n'est demandé que pour une liste d'apporteurs (IdApporteur1) autorisés.
  // Pas de demande sur un avenant : le client est déjà assuré.
  const showEmailConsent = canAskContactConsent && !isAvenant;
  // Déjà recueilli par l'étape « Votre devis est presque prêt » (après la tarification) : on ne le redemande pas
  const emailConsentDejaRecueilli = isConsentementEmailRecueilli(methods.watch('DemandeDevis.PixelConsent'));
  // Le consentement téléphonique n'est demandé que sur le canal direct.
  const showPhoneConsent = isDirect;

  // Question non affichée (courtier, etc.) : consentement email à false par défaut
  useEffect(() => {
    if (!showEmailConsent) {
      methods.setValue('DemandeDevis.EmailContactConsent', false);
    }
  }, [showEmailConsent, methods]);

  const logConsent = (data: FormSchemaType) => {
    if (!isDirect && !canAskContactConsent) return;
    const personne = data?.DemandeTarif?.ListePersonnes?.[0];
    mutationLogConsent.mutate({
      token: meta.token,
      canal: data?.DemandeTarif?.Police?.Canal ?? '',
      phone: personne?.TelPortable ?? '',
      email: personne?.Email ?? '',
      phone_contact_consent: showPhoneConsent
        ? (data?.DemandeDevis?.PhoneContactConsent ?? false)
        : (data?.DemandeDevis?.OptinRecontact ?? false),
      email_contact_consent: data?.DemandeDevis?.EmailContactConsent ?? false,
      type: 'souscription',
    });
  };

  const handleSubmit = async (data: FormSchemaType) => {
    const response = await mutationSaveDevis.mutateAsync(data);
    logConsent(data);
    return response?.data;
  };

  const handleOptinRecontact = async () => {
    const values = methods.getValues();
    const response = await mutationSaveDevis.mutateAsync(values);

    if (response?.data) {
      logConsent(values);
      saveData(response);
      methods.setValue('Devis', response?.data?.Devis);
      setSearchParams({ modal: 'optin-recontact' });
    }
  };

  const OptinRecontact = methods.watch('DemandeDevis.OptinRecontact');

  return (
    <SmartFormGlobal
      {...methods}
      className="mx-auto"
      onSubmit={handleSubmit}
    >
      <Question.Header>
        <OrdinateurTchat />
        <Question.Title>{getContent(informationInformations.title)}</Question.Title>
      </Question.Header>
      <CompanyFields label={getContent(informationEntreprise.inputLabel) as string} />

      <FieldInput
        autoComplete="email"
        label={getContent(informationInformations.labelEmail) as string}
        name="DemandeTarif.ListePersonnes.0.Email"
        type="email"
        validation={{
          pattern: {
            value: /\S+@\S+\.\S+/,
            message: 'Adresse e-mail invalide',
          },
          validate: {
            notCourtierEmail: validateNotCourtierEmail,
          },
        }}
      />

      <CourtierEmailWarning />

      <FieldInput
        autoComplete="email"
        label="Confirmer l'adresse e-mail"
        name="DemandeTarif.ListePersonnes.0.ConfirmEmail"
        type="email"
        validation={{
          validate: {
            isSame: (value) => value === methods.watch('DemandeTarif.ListePersonnes.0.Email') || 'Les adresses e-mail ne correspondent pas',
          },
        }}
      />

      {showEmailConsent && !emailConsentDejaRecueilli && (
        <>
          <BulleAide>
            <p><strong>{r(coordonneesContent.emailConsentIntro)}</strong></p>
            <p className="mb-4">{r(coordonneesContent.emailConsentReceive)}</p>
            <p><strong>{r(coordonneesContent.emailConsentWhyTitle)}</strong></p>
            <ul className="mb-4 space-y-1">
              <li className="flex items-start gap-2">
                <CheckIcon className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <span>{r(coordonneesContent.emailConsentBullet1)}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <span>{r(coordonneesContent.emailConsentBullet2)}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <span>{r(coordonneesContent.emailConsentBullet3)}</span>
              </li>
            </ul>
          </BulleAide>
          <div className="w-full">
            <FieldRadioBlock
              name="DemandeDevis.EmailContactConsent"
              options={[
                { value: true, label: "J'accepte" },
                { value: false, label: 'Je refuse' },
              ]}
              validation={{
                validate: (value) =>
                  value === true || value === false || 'Veuillez faire un choix',
              }}
            />
          </div>
        </>
      )}

      <FieldInput
        autoComplete="tel"
        description={getContent(informationInformations.descriptionTel) as string}
        label={getContent(informationInformations.labelTel) as string}
        name="DemandeTarif.ListePersonnes.0.TelPortable"
        placeholder="Exemple : 06 07 08 09 10"
        required={apporteur1Belair === 43397}
        validation={{
          pattern: {
            value: /^0[1-9]([-. ]?[0-9]{2}){4}$/,
            message: 'Numéro de téléphone invalide',
          },
          setValueAs: (value: string) => value?.replace(/\D/g, ''),
        }}
      />

      {showPhoneConsent && (
        <>
          <HorairesRecontact />
          <BulleAide title={r(coordonneesContent.phoneConsentTitle)}>
            <p>{r(coordonneesContent.phoneConsentSubtitle)}</p>
            <ul className="list-disc list-inside mb-4">
              <li>{r(coordonneesContent.phoneConsentBullet1)}</li>
              <li>{r(coordonneesContent.phoneConsentBullet2)}</li>
              <li>{r(coordonneesContent.phoneConsentBullet3)}</li>
            </ul>
          </BulleAide>
          <div className="w-full">
            <FieldRadioBlock
              name="DemandeDevis.PhoneContactConsent"
              options={[
                { value: true, label: "Oui, j'accepte d'être appelé" },
                { value: false, label: 'Non, continuer sans appel' },
              ]}
              validation={{
                required: 'Veuillez faire un choix',
              }}
            />
          </div>
          <ConsentRecontactLegal className="text-[13px]" />
        </>
      )}

      <RgpdInfoBloc />

      <SmartButton
        loading={mutationSaveDevis.isPending}
        onClick={OptinRecontact ? handleOptinRecontact : undefined}
        type={OptinRecontact ? 'button' : 'submit'}
      >
        {OptinRecontact ? 'Recevoir ma proposition' : 'Suivant'}
      </SmartButton>

      <ModalOptionRecontact />
    </SmartFormGlobal>
  );
}

function ModalOptionRecontact() {
  const { actions } = useGlobalForm();
  const methods = useFormContext<FormSchemaType>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isCourtier } = useSource();

  const handleSubmition = () => {
    actions.goNextQuestion();
  };

  return (
    <Modal
      className="h-[calc(100vh-40vh)] sm:max-h-[calc(100vh-125px)] sm:h-auto"
      onClose={() => {
        setSearchParams();
      }}
      open={searchParams.get('modal') === 'optin-recontact'}
      title="Votre proposition d'assurance"
    >
      <Modal.Body className="pt-4 pb-8 mt-4 border-t border-t-primary-light overflow-y-scroll text-sm">
        {!isCourtier && (
          <p>
            Votre proposition d'assurance a été envoyée sur l'adresse e-mail suivante :{' '}
            <span className="text-primary font-medium">{methods.watch('DemandeTarif.ListePersonnes.0.Email')}</span>
          </p>
        )}
        <SmartButton
          arrow={false}
          className="w-full mt-5"
          onClick={handleSubmition}
          size="sm"
          type="button"
        >
          Je finalise la souscription
        </SmartButton>

        <a
          className="text-center block mt-3 font-medium text-primary"
          href={methods.watch('Devis.UrlPDFDevis')}
          rel="noopener"
          target="_blank"
        >
          Télécharger la proposition d'assurance
        </a>

        <p className="mt-8">Retrouvez aussi en téléchargement :</p>

        <ul>
          <li>
            <a
              className="text-primary font-medium hover:underline"
              href={methods.watch('Devis.UrlPDFDocumentInformation')}
              rel="noopener"
              target="_blank"
            >
              Document d'information
            </a>
          </li>
          <li>
            <a
              className="text-primary font-medium hover:underline"
              href={methods.watch('Devis.UrlPDFDispositionsGenerales')}
              rel="noopener"
              target="_blank"
            >
              Dispositions Générales
            </a>
          </li>
        <li>
                      <a className="text-primary font-medium hover:underline" href={methods.watch('Devis.UrlPDFConventionAssistance')} rel="noopener" target="_blank">
                        Annexes Assistance
                      </a>
                    </li>
                </ul>
      </Modal.Body>
    </Modal>
  );
}
